import { Link, Outlet, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { LogOut, LayoutDashboard, Package, ShoppingCart, Users, BarChart3, CreditCard, Settings, FileText } from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { apiUrl } from "../utils/api";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";

const AdminLayout = () => {
  const location = useLocation();
  const { t } = useLanguage();
  const [username, setUsername] = useState("Admin");
  const [siteName, setSiteName] = useState("BeeMerce");

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(apiUrl("/api/v1/common/config"));
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            const { site_name, site_favicon } = result.data;
            if (site_name) setSiteName(site_name);
            
            // Set Page Title
            document.title = `${site_name || "BeeMerce"} Console`;

            // Set Favicon
            if (site_favicon) {
              const link = (document.getElementById('dynamic-favicon') as HTMLLinkElement) || 
                           document.querySelector("link[rel*='icon']") as HTMLLinkElement || 
                           document.createElement('link');
              
              const isSvg = site_favicon.toLowerCase().endsWith('.svg');
              link.type = isSvg ? 'image/svg+xml' : 'image/x-icon';
              link.rel = 'shortcut icon';
              link.href = site_favicon;
              
              if (!document.getElementById('dynamic-favicon')) {
                link.id = 'dynamic-favicon';
                document.getElementsByTagName('head')[0].appendChild(link);
              }
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch site config:", error);
      }
    };

    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem("admin_token");
        if (!token) return;

        const response = await fetch(apiUrl("/api/v1/admin/verify"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();
        if (result.success && result.data.user) {
          setUsername(result.data.user.username || "Admin");
        }
      } catch (error) {
        console.error("Failed to fetch user info:", error);
      }
    };

    fetchConfig();
    fetchUserInfo();
  }, []);

  const currentAdminPath = location.pathname.split('/')[1] || 'admin';

  const menuItems = [
    { path: `/${currentAdminPath}/dashboard`, label: t("admin.menu.dashboard"), icon: LayoutDashboard },
    { path: `/${currentAdminPath}/products`, label: t("admin.menu.products"), icon: Package },
    { path: `/${currentAdminPath}/orders`, label: t("admin.menu.orders"), icon: ShoppingCart },
    { path: `/${currentAdminPath}/customers`, label: t("admin.menu.customers"), icon: Users },
    { path: `/${currentAdminPath}/pages`, label: t("admin.menu.pages"), icon: FileText },
    { path: `/${currentAdminPath}/analytics`, label: t("admin.menu.analytics"), icon: BarChart3 },
    { path: `/${currentAdminPath}/payments`, label: t("admin.menu.payments"), icon: CreditCard },
    { path: `/${currentAdminPath}/settings`, label: t("admin.menu.settings"), icon: Settings },
  ];

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    window.location.href = `/${currentAdminPath}`;
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background gradients */}
      <div className="absolute top-[-160px] right-[-140px] w-[480px] h-[480px] rounded-full pointer-events-none z-0 opacity-55 bg-gradient-radial from-[rgba(245,17,182,0.25)] to-transparent" />
      <div className="absolute bottom-[-180px] left-[-160px] w-[480px] h-[480px] rounded-full pointer-events-none z-0 opacity-55 bg-gradient-radial from-[rgba(155,89,182,0.25)] to-transparent" />

      <div className="grid grid-cols-[260px_minmax(0,1fr)] min-h-screen relative z-10">
        {/* Sidebar */}
        <aside className="p-7 flex flex-col gap-7 bg-gradient-to-br from-[rgba(155,89,182,0.12)] to-[rgba(245,17,182,0.08)] dark:from-background dark:to-background/50 backdrop-blur-xl border-r border-border text-foreground">
          {/* Brand */}
          <Link to="/" className="flex flex-col gap-2.5 hover:opacity-80 transition-opacity">
            <div className="text-2xl font-serif tracking-wider">{siteName}</div>
            <div className="text-sm text-muted-foreground">{t("admin.panel")}</div>
          </Link>

          {/* Moved Controls: Theme & Language */}
          <div className="flex items-center justify-between p-2 rounded-xl bg-card/30 border border-border/50">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>

          {/* User info */}
          <div className="flex flex-col gap-2 p-3.5 rounded-xl bg-card shadow-md">
            <div className="font-semibold text-sm">{username}</div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="w-2 h-2 rounded-full bg-[#11F550] shadow-[0_0_0_3px_rgba(17,245,80,0.2)]" />
              Online
            </div>
          </div>

          {/* Menu */}
          <nav className="flex-1 flex flex-col gap-2.5">
            {menuItems.map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                    "text-sm font-medium",
                    isActive
                      ? "bg-[#F511B6] text-white shadow-[0_0_15px_rgba(245,17,182,0.4)] translate-x-1"
                      : "hover:bg-card/50 hover:shadow-sm hover:translate-x-1"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout button */}
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full justify-start gap-3"
          >
            <LogOut className="w-4 h-4" />
            {t("admin.logout")}
          </Button>
        </aside>

        {/* Main content */}
        <div className="flex flex-col text-foreground">
          {/* Header Removed as requested - duplicated with page content titles */}
          
          {/* Content area */}
          <main className="flex-1 p-8 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;